import React, { useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Info, MapPin, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Lease } from '../../types/leases';
import LeaseHistoryButton from '../Leases/LeaseHistoryButton';
import { isSpotRented } from '../../Helpers/parkingUtils';
import { ParkingArea, ParkingSpot } from '../../types/parking';
import { Building } from '../../types/protocols';
import { BaseComponentImpl } from '../../types/implementations';
import { translate } from '../../types/EnumTranslations';
import { RentableDetailSection, RentableDetailTitle, RentableLeaseSection, RentablePhotosSection } from '../../Components/RentableDetail/RentableDetail';
import InfoPanel from '../../Components/InfoPanel';
import { parkingSpotDisplayName } from '../../Helpers/parkingUtils';
import { suggestedLeaseStart } from '../../Helpers/parkingQueueUtils';
import { ParkingQueueEntry } from '../../types/parkingQueue';
import QueueCandidatesSection from '../ParkingQueue/QueueCandidatesSection';
import QueueLeaseChoice from '../ParkingQueue/QueueLeaseChoice';
import { useSpotQueueCheck } from '../../hooks/parkingQueue';
import { SpotRef } from '../../Helpers/parkingQueueUtils';

interface ParkingSpotDetailModalProps {
    /** False while a child modal (Redigera, Nytt hyresavtal, delete confirmation) is open. */
    show: boolean;
    onHide: () => void;
    building: Building;
    area: ParkingArea;
    spot: ParkingSpot;
    isAdmin: boolean;
    submitting: boolean;
    hasDrawing: boolean;
    isPlacedOnCurrentDrawing: boolean;
    /** Redigera; the marker is removed from there too ("Ta bort placering"). */
    onEdit: () => void;
    onDelete: () => void;
    /** Click-to-place for a spot that has no marker on the current drawing; the page closes the modal. Placed spots are moved with "Flytta markörer". */
    onStartPlace: () => void;
    /** Component page of the spot's charger. */
    chargerLink: string;
    /** The lease behind `spot.currentLeaseKey`, when loaded. */
    lease?: Lease;
    tenantName?: string;
    /** Hyresavtal for this object; absent for non-admins, who cannot open it. */
    leasesLink?: string;
    /** Nytt hyresavtal with any tenant; when someone in the queue matches the spot, the admin is asked first. */
    onNewLease: () => void;
    /** Admins: Hyr ut on an applicant in the Kö section; offered while the spot is free, or from the day after its lease ends. */
    onRentFromQueue: (entry: ParkingQueueEntry, reason?: string) => void;
    /** "Lägg till i kön" in the Kö section. */
    onAddToQueue: () => void;
    /** Bumped after a lease from the queue or a new entry, so an open Kö section reads the queue again. */
    queueReloadKey: number;
    /** Admins only: the object's ended leases, newest first ("Tidigare hyresgäster"). */
    endedLeases?: Lease[];
    getTenantName: (tenantKey: string) => string;
}

/**
 * Details, photos and actions for one spot, opened from the spot list or a marker.
 * Sections Uppgifter, Hyresavtal, Kö (admins) and Foton; Redigera and Ta bort sit in the header's ⋯ menu.
 */
const ParkingSpotDetailModal: React.FC<ParkingSpotDetailModalProps> = ({
    show, onHide, building, area, spot, isAdmin, submitting, hasDrawing, isPlacedOnCurrentDrawing,
    onEdit, onDelete, onStartPlace, chargerLink,
    lease, tenantName, leasesLink, onNewLease, onRentFromQueue, onAddToQueue, queueReloadKey, endedLeases, getTenantName
}) => {
    // Here rather than in the section: the body unmounts while the lease form is open, and the section stays open on return.
    const [queueExpanded, setQueueExpanded] = useState(false);
    const queueSectionRef = useRef<HTMLElement>(null);
    const queueCheck = useSpotQueueCheck();
    // Matching applicants found on Nytt hyresavtal: the admin picks the queue or any tenant.
    const [queueChoiceCount, setQueueChoiceCount] = useState<number | null>(null);
    const spotRef: SpotRef = { buildingKey: building.key, areaKey: area.key, spotKey: spot.key, spot };

    const chargerName = useMemo(() => {
        if (!spot.chargerComponentKey) return null;
        const component = building.model.structure?.components?.[spot.chargerComponentKey];
        return component ? new BaseComponentImpl(component).displayName(building) : 'Laddare saknas i byggnaden';
    }, [spot.chargerComponentKey, building]);
    const chargerExists = !!spot.chargerComponentKey && !!building.model.structure?.components?.[spot.chargerComponentKey];

    const rented = isSpotRented(spot);
    const ceilingHeight = spot.ceilingHeight ?? area.defaultCeilingHeight;
    const photos = spot.photos || {};
    // A rented spot goes to the queue only from the day after its lease ends; tills vidare leaves no such day.
    const queueStart = !rented ? null : lease?.endDate ? suggestedLeaseStart([lease]) : undefined;

    const startNewLease = async () => {
        setQueueChoiceCount(null);
        const count = await queueCheck.count(spotRef);
        if (count > 0) setQueueChoiceCount(count);
        else onNewLease();
    };

    const pickFromQueue = () => {
        setQueueChoiceCount(null);
        setQueueExpanded(true);
        requestAnimationFrame(() => queueSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
    };

    const pickAnyTenant = () => {
        setQueueChoiceCount(null);
        onNewLease();
    };

    const placementValue = !hasDrawing
        ? 'Ingen ritning'
        : isPlacedOnCurrentDrawing
            ? 'Placerad på ritningen'
            : (
                <span className="rentable-detail-inline">
                    <span style={{ color: '#7a5b00' }}>Ej placerad</span>
                    {isAdmin && (
                        <button type="button" className="rentable-detail-link" onClick={onStartPlace} disabled={submitting}>
                            <MapPin size={14} />Placera på ritningen
                        </button>
                    )}
                </span>
            );

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <RentableDetailTitle
                    name={parkingSpotDisplayName(spot)}
                    status={{ rented, label: rented ? 'Uthyrd' : 'Ledig' }}
                    onEdit={isAdmin ? onEdit : undefined}
                    onDelete={isAdmin ? onDelete : undefined}
                    deleteLabel="Ta bort plats"
                    disabled={submitting}
                />
            </Modal.Header>
            <Modal.Body>
                <RentableDetailSection icon={<Info size={16} />} title="Uppgifter">
                    <InfoPanel
                        items={[
                            { label: 'Typ', value: translate(spot.type) },
                            { label: 'Fri höjd', value: ceilingHeight ? `${ceilingHeight.toFixed(2)} m${spot.ceilingHeight ? '' : ' (områdets)'}` : '-' },
                            {
                                label: 'Laddare',
                                value: chargerExists && chargerName
                                    ? <Link to={chargerLink} className="rentable-detail-link"><Zap size={14} />{chargerName}</Link>
                                    : (chargerName ?? 'Ingen'),
                            },
                            { label: 'Motorvärmaruttag', value: spot.hasOutlet ? (spot.outletIdentifier ? `Ja, uttag ${spot.outletIdentifier}` : 'Ja') : 'Nej' },
                            { label: 'Placering', value: placementValue },
                            ...(spot.comment ? [{ label: 'Kommentar', value: spot.comment }] : []),
                        ]}
                    />
                </RentableDetailSection>

                <RentableLeaseSection
                    rented={rented}
                    lease={lease}
                    tenantName={tenantName}
                    leasesLink={leasesLink}
                    rentedText="Uthyrd"
                    vacantText="Platsen är ledig."
                    onNewLease={isAdmin ? () => { startNewLease().catch(() => undefined); } : undefined}
                    submitting={submitting || queueCheck.checking}
                    links={isAdmin && endedLeases && (
                        <LeaseHistoryButton buildingKey={building.key} leases={endedLeases} getTenantName={getTenantName} />
                    )}
                >
                    {queueChoiceCount !== null && !rented && (
                        <QueueLeaseChoice count={queueChoiceCount} onPickFromQueue={pickFromQueue}
                            onPickAnyTenant={pickAnyTenant} onCancel={() => setQueueChoiceCount(null)} />
                    )}
                </RentableLeaseSection>

                {isAdmin && (
                    <QueueCandidatesSection
                        sectionRef={queueSectionRef}
                        spotRef={spotRef}
                        expanded={queueExpanded}
                        onToggle={() => setQueueExpanded(open => !open)}
                        rentFrom={queueStart}
                        notRentableNote={lease && !lease.endDate ? 'Platsen är uthyrd tills vidare.' : undefined}
                        reloadKey={queueReloadKey}
                        onPick={onRentFromQueue}
                        onAddToQueue={onAddToQueue}
                        submitting={submitting}
                    />
                )}

                <RentablePhotosSection
                    photos={photos}
                    buildingKey={building.key}
                />
            </Modal.Body>
        </Modal>
    );
};

export default ParkingSpotDetailModal;
